import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, FileDown, TrendingUp, Users2,
  Building2, Trophy, Target, Filter,
} from "lucide-react";
import api from "@/lib/api";

const STATUS_OPTIONS = [
  { v: "",       t: "Todos" },
  { v: "active", t: "Activos" },
  { v: "closed", t: "Fechados" },
];

const METRIC_OPTIONS = [
  { v: "",                          t: "Todas as métricas" },
  { v: "contract_value",            t: "Valor do contrato" },
  { v: "deposit_total_paid",        t: "Caução paga" },
  { v: "contracts_count",           t: "Contratos fechados" },
  { v: "monthly_value_annualised",  t: "Valor do contrato (legado)" },
];

function fmtEUR(v) {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2,
  }).format(Number(v || 0));
}
function fmtInt(v) {
  return new Intl.NumberFormat("pt-PT").format(Math.round(Number(v || 0)));
}
function fmtPct(v) {
  return `${Number(v || 0).toFixed(1)}%`;
}
function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso
    : d.toLocaleString("pt-PT", { day: "2-digit", month: "short",
                                    hour: "2-digit", minute: "2-digit" });
}

function KpiCard({ label, value, icon: Icon, tone = "navy", testid }) {
  const tones = {
    navy:    { bg: "bg-foreground text-background", labelCls: "text-background/50", valueCls: "text-background", iconCls: "text-background/60" },
    bronze:  { bg: "bg-card border border-border shadow-sm", labelCls: "text-muted-foreground", valueCls: "text-foreground", iconCls: "text-muted-foreground" },
    ivory:   { bg: "bg-card border border-border shadow-sm", labelCls: "text-muted-foreground", valueCls: "text-foreground", iconCls: "text-muted-foreground" },
    sage:    { bg: "bg-card border border-[hsl(var(--success))]/25 shadow-sm", labelCls: "text-[hsl(var(--success))]", valueCls: "text-[hsl(var(--success))]", iconCls: "text-emerald-500" },
  };
  const s = tones[tone];
  return (
    <div className={`rounded-lg p-5 ${s.bg}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className={`kpi-label ${s.labelCls}`}>{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${s.iconCls}`} strokeWidth={1.5} />}
      </div>
      <div className={`kpi-value ${s.valueCls} tabular-nums`}>{value}</div>
    </div>
  );
}

function StatusPill({ hit }) {
  if (hit) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] px-2 py-0.5 rounded-full border border-[hsl(var(--success))]/25">
        <Trophy className="w-3 h-3" /> Atingido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] px-2 py-0.5 rounded-full border border-[hsl(var(--warning))]/25">
      <Target className="w-3 h-3" /> Em curso
    </span>
  );
}

function ProgressBar({ pct }) {
  const clamped = Math.min(100, Math.max(0, Number(pct || 0)));
  const bg = clamped >= 100 ? "bg-emerald-500"
    : clamped >= 70 ? "bg-foreground"
    : clamped >= 40 ? "bg-amber-500"
    : "bg-rose-400";
  return (
    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
      <div className={`h-full ${bg}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

export default function BonusesOverview() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  // Filters
  const [year, setYear] = useState("");
  const [status, setStatus] = useState("active");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [metric, setMetric] = useState("");
  const [operatorId, setOperatorId] = useState("");
  // Options loaded once
  const [officesList, setOfficesList] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (start || end) {
        if (start) params.start = start;
        if (end)   params.end = end;
      } else if (year) {
        params.year = Number(year);
      }
      if (status)      params.status = status;
      if (officeId)    params.office_id = officeId;
      if (metric)      params.target_metric = metric;
      if (operatorId)  params.operator_id = operatorId;
      const { data } = await api.get("/bonuses/overview", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar bónus.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch the office + operators list once (used by the filter dropdowns)
  useEffect(() => {
    (async () => {
      try {
        const [{ data: offices }, { data: users }] = await Promise.all([
          api.get("/offices"),
          api.get("/users"),
        ]);
        setOfficesList(Array.isArray(offices) ? offices : (offices?.offices || []));
        const list = Array.isArray(users) ? users : (users?.users || []);
        setOperatorsList(list.filter((u) => u.active !== false && u.email));
      } catch {
        // silent — filters degrade gracefully to text-only
      }
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [year, status, start, end, officeId, metric, operatorId]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (start || end) {
        if (start) params.append("start", start);
        if (end)   params.append("end", end);
      } else if (year) {
        params.append("year", year);
      }
      if (status)     params.append("status", status);
      if (officeId)   params.append("office_id", officeId);
      if (metric)     params.append("target_metric", metric);
      if (operatorId) params.append("operator_id", operatorId);
      const resp = await api.get(`/bonuses/overview.pdf?${params.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = (start || end) ? `${start || "inicio"}_a_${end || "fim"}`
        : year ? String(year) : "todos";
      a.download = `Relatorio-Bonus-${stamp}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      toast.success("Relatório PDF descarregado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar PDF.");
    } finally {
      setBusy(false);
    }
  };

  const clearFilters = () => {
    setYear(""); setStatus(""); setStart(""); setEnd("");
    setOfficeId(""); setMetric(""); setOperatorId("");
  };
  const activeCount = [status, start, end, officeId, metric, operatorId, year]
    .filter((v) => !!v).length;

  const totals = data?.totals || {};
  const offices = data?.offices || [];
  const objectives = data?.objectives || [];
  const topOps = data?.top_operators || [];

  const currentYear = new Date().getFullYear();
  const yearOpts = useMemo(() => {
    const arr = [];
    for (let y = currentYear + 1; y >= currentYear - 3; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-24 text-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> A carregar…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4" data-testid="bonuses-overview-page">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground font-semibold">
            Prémios · Visão global
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold text-foreground mt-1 leading-tight">
            Bónus por escritório e operador
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Consolidado de todos os objectivos activos e concluídos. Actualizado em tempo real
            à medida que os contratos são validados e as cauções pagas.
            <br />Última actualização: <b className="text-foreground">{fmtDT(data?.generated_at)}</b>.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Basic filters row */}
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <select
              value={year} onChange={(e) => { setYear(e.target.value); setStart(""); setEnd(""); }}
              className="text-sm bg-transparent focus:outline-none pr-2"
              disabled={!!(start || end)}
              data-testid="bonuses-year-filter"
            >
              <option value="">Todos os anos</option>
              {yearOpts.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
            <span className="text-[#CBD5E1]">·</span>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="text-sm bg-transparent focus:outline-none pr-2"
              data-testid="bonuses-status-filter"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.v} value={s.v}>{s.t}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border ${
              showAdvanced || activeCount > 2
                ? "border-primary bg-muted text-foreground"
                : "border-border bg-card text-foreground hover:bg-muted/40"
            }`}
            data-testid="bonuses-advanced-toggle"
          >
            <Filter className="w-3.5 h-3.5" />
            {showAdvanced ? "Fechar filtros" : "Mais filtros"}
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-foreground text-background text-[10px] font-bold ml-0.5">
                {activeCount}
              </span>
            )}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-border bg-card hover:bg-muted/40"
            data-testid="bonuses-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
          <button
            onClick={downloadPdf}
            disabled={busy}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-foreground text-background hover:bg-slate-800 disabled:opacity-50"
            data-testid="bonuses-pdf-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Descarregar PDF
          </button>
        </div>
      </div>

      {/* Advanced filters row */}
      {showAdvanced && (
        <div className="bg-card border border-border rounded-xl p-4 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3 items-end"
              data-testid="bonuses-advanced-panel">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">
              Intervalo de datas
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date" value={start} onChange={(e) => { setStart(e.target.value); if (e.target.value) setYear(""); }}
                className="flex-1 text-sm border border-border rounded-md px-2 py-1.5 bg-card"
                data-testid="bonuses-start-date"
              />
              <span className="text-muted-foreground text-xs">→</span>
              <input
                type="date" value={end} onChange={(e) => { setEnd(e.target.value); if (e.target.value) setYear(""); }}
                className="flex-1 text-sm border border-border rounded-md px-2 py-1.5 bg-card"
                data-testid="bonuses-end-date"
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Sobrepõe o filtro anual. Objectivos que <b>intersectam</b> o intervalo são incluídos.
            </p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">
              Escritório
            </label>
            <select
              value={officeId} onChange={(e) => setOfficeId(e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-card"
              data-testid="bonuses-office-filter"
            >
              <option value="">Todos</option>
              {officesList.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">
              Métrica
            </label>
            <select
              value={metric} onChange={(e) => setMetric(e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-card"
              data-testid="bonuses-metric-filter"
            >
              {METRIC_OPTIONS.map((m) => (
                <option key={m.v} value={m.v}>{m.t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">
              Operador
            </label>
            <select
              value={operatorId} onChange={(e) => setOperatorId(e.target.value)}
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-card"
              data-testid="bonuses-operator-filter"
            >
              <option value="">Todos</option>
              {operatorsList.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end">
            {activeCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs text-[hsl(var(--danger))] hover:underline font-semibold"
                data-testid="bonuses-clear-filters"
              >
                Limpar tudo ({activeCount})
              </button>
            )}
          </div>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Escritórios activos" value={fmtInt(totals.offices_count)}
                    icon={Building2} tone="ivory" testid="kpi-offices" />
        <KpiCard label="Objectivos"          value={fmtInt(totals.objectives_count)}
                    icon={Target}    tone="ivory" testid="kpi-objectives" />
        <KpiCard label="Pool de prémios"     value={fmtEUR(totals.bonus_pool)}
                    icon={TrendingUp} tone="navy" testid="kpi-pool" />
        <KpiCard label="Operadores pagos"    value={fmtInt(totals.operators_paid)}
                    icon={Users2}   tone="bronze" testid="kpi-operators" />
      </div>

      {/* Aggregate summary */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-8 flex flex-wrap gap-8 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Meta agregada</div>
          <div className="text-lg font-semibold text-foreground mt-1">{fmtEUR(totals.target_sum)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Realizado</div>
          <div className="text-lg font-semibold text-foreground mt-1">{fmtEUR(totals.achieved_sum)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Atingimento</div>
          <div className="text-lg font-semibold text-[hsl(var(--success))] mt-1">{fmtPct(totals.achievement_pct)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Progresso</div>
          <div className="text-lg font-semibold text-foreground mt-1">
            <span className="text-[hsl(var(--success))]">{totals.objectives_hit || 0}</span>
            <span className="text-muted-foreground"> / {totals.objectives_count || 0}</span>
            <span className="text-[10px] text-muted-foreground ml-2 uppercase tracking-wide font-medium">atingidos</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Offices ranking */}
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6" data-testid="offices-ranking">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-5 h-5 text-foreground" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                Ranking por escritório
              </div>
              <h3 className="text-xl font-semibold text-foreground">Contribuição para o pool</h3>
            </div>
          </div>
          {offices.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Sem escritórios com objectivos no período seleccionado.
            </div>
          ) : (
            <div className="space-y-3">
              {offices.map((o, i) => (
                <div key={o.office_id + i} className="flex items-center gap-3" data-testid={`office-row-${o.office_id}`}>
                  <div className="w-6 text-center text-xs font-bold text-muted-foreground tabular-nums">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="font-semibold text-foreground truncate">{o.office_name}</span>
                      <span className="text-sm text-foreground tabular-nums font-semibold">{fmtEUR(o.bonus_pool)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{o.objectives_count} obj.</span>
                      <span>·</span>
                      <span>Meta: <span className="text-foreground">{fmtEUR(o.target_sum)}</span></span>
                      <span>·</span>
                      <span>Realizado: <span className="text-foreground">{fmtEUR(o.achieved_sum)}</span></span>
                      <span className="ml-auto tabular-nums">{fmtPct(o.achievement_pct)}</span>
                    </div>
                    <div className="mt-1.5"><ProgressBar pct={o.achievement_pct} /></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top operators */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="top-operators">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-5 h-5 text-foreground" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                Top 10 · Agregado
              </div>
              <h3 className="text-xl font-semibold text-foreground">Operadores</h3>
            </div>
          </div>
          {topOps.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Ainda não há operadores com prémios.
            </div>
          ) : (
            <div className="space-y-2">
              {topOps.map((op, i) => {
                const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : "";
                return (
                  <div key={op.operator_id + i}
                        className="flex items-center gap-3 py-2 border-b border-border/60 last:border-b-0"
                        data-testid={`top-op-${op.operator_id}`}>
                    <div className="w-7 text-center">
                      {medal ? <span className="text-lg">{medal}</span>
                        : <span className="text-xs font-semibold text-muted-foreground">#{i + 1}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground truncate">
                        {op.operator_name || op.operator_email || "—"}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {op.converted_count} convertidos · {fmtEUR(op.contributed_value)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-foreground tabular-nums">
                        {fmtEUR(op.bonus_amount)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Objectives table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="objectives-table">
        <div className="p-6 border-b border-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Detalhe por objectivo
          </div>
          <h3 className="text-xl font-semibold text-foreground">
            {objectives.length} objectivo{objectives.length === 1 ? "" : "s"} no período
          </h3>
        </div>
        {objectives.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Sem objectivos no período seleccionado.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-6 py-3">Objectivo</th>
                <th className="text-left px-6 py-3">Escritório</th>
                <th className="text-left px-6 py-3">Período</th>
                <th className="text-right px-6 py-3">Meta</th>
                <th className="text-right px-6 py-3">Realizado</th>
                <th className="text-right px-6 py-3">%</th>
                <th className="text-right px-6 py-3">Prémios</th>
                <th className="text-center px-6 py-3">Estado</th>
                <th className="text-right px-6 py-3">Acções</th>
              </tr>
            </thead>
            <tbody>
              {objectives.map((r) => {
                const a = r.achievement || {};
                const oid = r.objective?.id || r.objective_name;
                return (
                  <tr key={oid} className="border-t border-border/60 hover:bg-muted/40" data-testid={`obj-row-${oid}`}>
                    <td className="px-6 py-3 font-semibold text-foreground">{r.objective_name}</td>
                    <td className="px-6 py-3 text-foreground">{r.office_name}</td>
                    <td className="px-6 py-3 text-xs text-muted-foreground">{r.period_start} → {r.period_end}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{fmtEUR(a.target_value)}</td>
                    <td className="px-6 py-3 text-right tabular-nums font-semibold">{fmtEUR(a.achieved_value)}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{fmtPct(a.achievement_percentage)}</td>
                    <td className="px-6 py-3 text-right tabular-nums font-bold text-foreground">{fmtEUR(r.total_bonus_pool)}</td>
                    <td className="px-6 py-3 text-center"><StatusPill hit={a.target_met} /></td>
                    <td className="px-6 py-3 text-right">
                      <Link
                        to={`/objectives/${r.objective?.id}`}
                        className="text-xs text-foreground hover:underline font-semibold"
                        data-testid={`obj-detail-${oid}`}
                      >
                        Detalhe →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
